import {
  BUILT_IN_SOURCE_LOCALE,
  localeForFormatting,
} from "./preparedLocales";
import { isInstallableLocale } from "./localeRegistry";

export function activeFormattingLocale(): string {
  if (typeof document === "undefined") {
    return localeForFormatting(BUILT_IN_SOURCE_LOCALE);
  }
  const active = document.documentElement.dataset.kavooshLocale;
  return localeForFormatting(
    isInstallableLocale(active) ? active : BUILT_IN_SOURCE_LOCALE,
  );
}

export function formatActiveDateTime(
  value: string | number | Date,
  options: Intl.DateTimeFormatOptions,
): string {
  return activeDateTimeFormatter(activeFormattingLocale(), { ...options, ...(typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value) && !options.timeZone ? { timeZone: "UTC" } : {}) })
    .format(new Date(value));
}

export function formatActiveRelativeTime(
  value: number,
  unit: Intl.RelativeTimeFormatUnit,
): string {
  return new Intl.RelativeTimeFormat(activeFormattingLocale(), {
    numeric: "auto",
  }).format(value, unit);
}

/** Account presentation preference only; never changes timestamp instants. */
export function setActiveTimeZone(value: string | null): void {
  if (typeof document === "undefined") return;
  if (value === null) {
    delete document.documentElement.dataset.kavooshTimeZone;
    window.dispatchEvent(new Event("kavoosh:formatting-change"));
    return;
  }
  let timeZone = "UTC";
  if (value) {
    try { new Intl.DateTimeFormat(activeFormattingLocale(), { timeZone: value }); timeZone = value; }
    catch { /* Invalid profile preferences use the canonical UTC fallback. */ }
  }
  document.documentElement.dataset.kavooshTimeZone = timeZone;
  window.dispatchEvent(new Event("kavoosh:formatting-change"));
}

export function activeTimeZone(): string {
  return (typeof document === "undefined" ? undefined : document.documentElement.dataset.kavooshTimeZone)
    ?? new Intl.DateTimeFormat(activeFormattingLocale()).resolvedOptions().timeZone;
}

export function activeDateTimeFormatter(locale: string | undefined, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  // Callers can explicitly preserve calendar-only values with UTC.
  return new Intl.DateTimeFormat(locale, {
    ...options,
    timeZone: options.timeZone ?? activeTimeZone(),
  });
}

export function formatActiveNumber(value: number, options?: Intl.NumberFormatOptions): string {
  return new Intl.NumberFormat(activeFormattingLocale(), options).format(value);
}
